/**
 * Error Boundary Component
 * 
 * Catches React errors and displays fallback UI
 * Prevents entire app from crashing
 */

import React, { Component, ReactNode } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, Download } from 'lucide-react';

const logError = (error: Error | unknown, errorInfo?: React.ErrorInfo) => {
  const err = error instanceof Error ? error : null;
  const errorLog = {
    id: Date.now().toString(),
    timestamp: Date.now(),
    name: err?.name ?? (typeof error === 'object' && error !== null && 'name' in error ? String((error as { name: unknown }).name) : 'Error'),
    message: err?.message ?? (typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : String(error)),
    stack: err?.stack ?? (typeof error === 'object' && error !== null && 'stack' in error ? String((error as { stack: unknown }).stack) : undefined),
    componentStack: errorInfo?.componentStack,
  };
  try {
    const logs = JSON.parse(localStorage.getItem('tension_error_logs') || '[]');
    logs.push(errorLog);
    localStorage.setItem('tension_error_logs', JSON.stringify(logs.slice(-50)));
  } catch (e) {
    console.error('Failed to store error log:', e);
  }
  return errorLog;
};

const getFriendlyErrorMessage = (error: Error | unknown): string => {
  if (error instanceof Error && error.message) {
    const msg = error.message.toLowerCase();
    if (msg.includes('fetch')) return 'Network connection error. Please check your internet connection.';
    if (msg.includes('json')) return 'Data format error. Please try again.';
    if (msg.includes('object') && msg.includes('react child')) return 'A display error occurred. Please refresh the page.';
    return error.message.slice(0, 200);
  }
  if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message.slice(0, 200);
  }
  return 'Something went wrong. Please try again.';
};

const isRecoverableError = (error: Error | null): boolean => {
  if (!error || !(error instanceof Error)) return false;
  return error.message.includes('fetch') || error.message.includes('network');
};

const attemptRecovery = async (error: Error): Promise<boolean> => {
  // Simple recovery attempt
  await new Promise(resolve => setTimeout(resolve, 1000));
  return false;
};

const exportErrorLogs = () => {
  try {
    const logs = localStorage.getItem('tension_error_logs') || '[]';
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-errors-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Failed to export error logs:', e);
  }
};

type ErrorLog = ReturnType<typeof logError>;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  level?: 'app' | 'page' | 'component';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorLog: ErrorLog | null;
  showDetails: boolean;
  isRecovering: boolean;
  suppressedError: boolean; // Track if we're suppressing errors
}

export class ErrorBoundary extends Component<Props, State> {
  private errorCount = 0; // Track error count to prevent infinite loops
  private lastErrorTime = 0; // Track when last error occurred
  private errorThreshold = 5; // Increased from 2 to 5 - less sensitive
  private isProcessingError = false; // Prevent concurrent error processing
  private infiniteLoopDetected = false; // Flag to completely stop error handling
  private errorWindow = 2000; // Increased from 500ms to 2000ms - wider time window
  private circuitBreakerTripped = false; // Circuit breaker to stop all error processing

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorLog: null,
      showDetails: false,
      isRecovering: false,
      suppressedError: false,
    };
  }

  // Static version of isFigmaIframeError - MUST be defined before getDerivedStateFromError
  private static isFigmaIframeErrorStatic(error: Error): boolean {
    const message = error.message || error.toString();
    const stack = error.stack || '';
    const name = error.name || '';
    const combined = (message + ' ' + stack + ' ' + name).toLowerCase();
    
    // Figma Make error patterns
    const figmaErrorPatterns = [
      'illegal constructor',
      'iframemessageaborterror',
      'message port was destroyed',
      'message aborted',
      'setupmessagechannel',
      'figma.com',
      'webpack-artifacts',
      'figma_app',
      'messagechannel',
      'messageport',
    ];
    
    return figmaErrorPatterns.some(pattern => combined.includes(pattern));
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a Figma error before setting error state
    if (ErrorBoundary.isFigmaIframeErrorStatic(error)) {
      console.debug('🔇 Suppressed Figma Make iframe error in getDerivedStateFromError:', error.message);
      return {
        suppressedError: true,
        hasError: false,
      };
    }

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Prevent concurrent error processing
    if (this.isProcessingError) {
      console.debug('⏭️ Skipping concurrent error processing');
      return;
    }

    // Prevent infinite loop: if we've had more than threshold errors in the error window, stop
    const now = Date.now();
    if (now - this.lastErrorTime < this.errorWindow) {
      this.errorCount++;
      if (this.errorCount > this.errorThreshold) {
        if (import.meta.env.DEV) {
          console.warn('⚠️ Error boundary detected potential infinite loop, stopping error processing');
          console.warn(`   ${this.errorCount} errors within ${this.errorWindow}ms window`);
          console.warn('   This is a protective measure - check for rendering loops in the component tree');
          console.warn('   Last error:', error.message);
        }
        // Don't process any more errors - just let the error UI show
        this.infiniteLoopDetected = true;
        return;
      }
    } else {
      this.errorCount = 1;
    }
    this.lastErrorTime = now;

    // Ignore Figma Make iframe communication errors and secure storage errors
    if (this.isFigmaIframeError(error)) {
      console.debug('🔇 Suppressed error:', error.message);
      // DO NOT call setState here - it causes infinite loops!
      return;
    }

    // Mark as processing to prevent concurrent calls
    this.isProcessingError = true;

    try {
      // Log error
      const errorLog = logError(error, errorInfo);
      
      // Update state with error info (no timeout needed since we're already in catch)
      this.setState({
        errorInfo,
        errorLog,
      });

      // Call optional error handler
      this.props.onError?.(error, errorInfo);

      // Log to console with full error details in development
      if (import.meta.env.DEV) {
        console.error('🔴 Error Boundary caught error:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo?.componentStack
        });
      }
    } finally {
      // Always reset processing flag
      this.isProcessingError = false;
    }
  }

  // Check if error is from Figma Make's iframe system
  private isFigmaIframeError(error: Error): boolean {
    return ErrorBoundary.isFigmaIframeErrorStatic(error);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorLog: null,
      showDetails: false,
      isRecovering: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleAttemptRecovery = async () => {
    const { error } = this.state;
    if (!error) return;

    this.setState({ isRecovering: true });

    const recovered = error instanceof Error ? await attemptRecovery(error) : false;
    
    if (recovered) {
      this.handleReset();
    } else {
      this.setState({ isRecovering: false });
    }
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    const { hasError, error, errorInfo, showDetails, isRecovering } = this.state;
    const { children, fallback, level = 'component' } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Default error UI
    const friendlyMessage = error != null ? getFriendlyErrorMessage(error) : 'An unexpected error occurred';
    const isRecoverable = error != null ? isRecoverableError(error instanceof Error ? error : null) : false;

    // Component-level error (minimal UI)
    if (level === 'component') {
      return (
        <div className="tension-glow p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-900">Component Error</p>
              <p className="text-sm text-red-700 mt-1">{friendlyMessage}</p>
              <button
                onClick={this.handleReset}
                className="text-sm text-red-600 hover:text-red-800 font-medium mt-2 underline"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Page/App-level error (full UI)
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full space-y-6">
          <Card className="p-8 space-y-6">
            {/* Icon & Title */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-light text-neutral-900">
                  {level === 'app' ? 'App Error' : 'Page Error'}
                </h1>
                <p className="text-neutral-600">
                  {friendlyMessage}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {isRecoverable && (
                <Button
                  onClick={this.handleAttemptRecovery}
                  disabled={isRecovering}
                  className="w-full"
                  size="lg"
                >
                  {isRecovering ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Recovering...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Attempt Recovery
                    </>
                  )}
                </Button>
              )}
              
              <Button
                onClick={this.handleReload}
                variant={isRecoverable ? 'outline' : 'default'}
                className="w-full"
                size="lg"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </Button>

              {level !== 'app' && (
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <Home className="w-4 h-4" />
                  Go to Home
                </Button>
              )}
            </div>

            {/* Error Details (Development) */}
            {process.env.NODE_ENV === 'development' && error != null && (
              <div className="space-y-2">
                <button
                  onClick={this.toggleDetails}
                  className="flex items-center justify-between w-full px-4 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  <span className="text-sm font-medium text-neutral-700">
                    Error Details (Development)
                  </span>
                  {showDetails ? (
                    <ChevronUp className="w-4 h-4 text-neutral-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-neutral-600" />
                  )}
                </button>

                {showDetails && (
                  <div className="space-y-3 p-4 bg-neutral-900 rounded-lg text-xs font-mono text-neutral-100 overflow-x-auto">
                    <div>
                      <p className="text-red-400 font-bold mb-1">Error:</p>
                      <p className="text-red-300">{error instanceof Error ? error.toString() : JSON.stringify(error).slice(0, 500)}</p>
                    </div>
                    
                    {error instanceof Error && error.stack && (
                      <div>
                        <p className="text-yellow-400 font-bold mb-1">Stack:</p>
                        <pre className="whitespace-pre-wrap text-yellow-300">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    
                    {errorInfo?.componentStack && (
                      <div>
                        <p className="text-blue-400 font-bold mb-1">Component Stack:</p>
                        <pre className="whitespace-pre-wrap text-blue-300">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    <Button
                      onClick={exportErrorLogs}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                    >
                      <Download className="w-3 h-3" />
                      Export Error Logs
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Help Text */}
            <div className="pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 text-center">
                This error has been logged. If the problem persists, please contact support.
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }
}

/**
 * Hook-based error boundary wrapper
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  level?: 'app' | 'page' | 'component'
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary level={level}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}